import { ApiClient, ClientsApi, RequestsApi, UsersApi, HealthApi } from '@enrichment/enrichment-api-client';

/**
 * URL de base de l'API Enrichment
 * 
 * La valeur est déterminée par priorité :
 * 1. Variable d'environnement `VITE_API_BASE_URL` (si définie)
 * 2. URL par défaut de l'environnement de développement
 * 
 * URLs par environnement :
 * - Dev: https://enrichment-app-api-dev-n5fu3zjaaa-ez.a.run.app/
 * - Preprod: https://enrichment-app-api-preprod-162542643484.europe-west4.run.app/
 * - Prod: (à définir)
 * 
 * En production, la variable `VITE_API_BASE_URL` doit être définie dans GitLab CI/CD
 * pour chaque environnement (dev, staging, production).
 */
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://enrichment-app-api-dev-n5fu3zjaaa-ez.a.run.app/';

/**
 * Instance principale du client API
 * Configurée avec l'URL de base déterminée ci-dessus
 */
const apiClient = new ApiClient(API_BASE_URL);

/**
 * Configure l'authentification HTTP Basic pour le client API
 * 
 * Cette fonction met à jour les credentials d'authentification HTTP Basic
 * utilisés par toutes les requêtes API générées par le client.
 * 
 * @param username - Nom d'utilisateur pour l'authentification HTTP Basic
 * @param password - Mot de passe pour l'authentification HTTP Basic
 * 
 * @example
 * ```typescript
 * configureAuth('myuser', 'mypassword');
 * ```
 */
const configureAuth = (username: string, password: string) => {
  const HTTPBasic = apiClient.authentications['HTTPBasic'];
  if (HTTPBasic) {
    HTTPBasic.username = username;
    HTTPBasic.password = password;
  }
};

/**
 * Credentials d'authentification API depuis les variables d'environnement
 * 
 * Comportement de fallback :
 * - Si `VITE_API_USERNAME` est définie → utilise sa valeur
 * - Sinon → utilise 'test' (pour développement local)
 * 
 * Même logique pour `VITE_API_PASSWORD`.
 * 
 * En production (preprod/prod), ces variables doivent être définies dans GitLab CI/CD
 * avec le scope "Environment: staging" ou "Environment: production" pour être accessibles.
 * 
 * @see README.md pour la configuration détaillée des variables d'environnement
 */
const API_USERNAME = import.meta.env.VITE_API_USERNAME || 'test';
const API_PASSWORD = import.meta.env.VITE_API_PASSWORD || 'test';

/**
 * Configuration initiale de l'authentification avec les credentials de l'environnement
 * 
 * Les credentials sont chargés depuis les variables d'environnement (ou utilisent
 * les valeurs par défaut 'test'/'test' pour le développement local).
 * 
 * @todo Remplacer par authentification via token JWT une fois l'API mise à jour
 *       pour une sécurité renforcée et une meilleure intégration avec Firebase Auth
 */
configureAuth(API_USERNAME, API_PASSWORD);

/**
 * Génère le header Authorization pour l'authentification HTTP Basic
 * 
 * Cette fonction est utilisée pour les appels HTTP directs (fetch) qui ne passent
 * pas par le client API généré. Elle encode les credentials en base64 selon le
 * standard HTTP Basic Authentication (RFC 7617).
 * 
 * @returns Header Authorization au format "Basic <base64(username:password)>"
 * 
 * @example
 * ```typescript
 * const authHeader = getBasicAuthHeader();
 * // authHeader = "Basic dGVzdDp0ZXN0" (pour test:test)
 * 
 * fetch(apiUrl, {
 *   headers: {
 *     'Authorization': authHeader,
 *     'Content-Type': 'application/json'
 *   }
 * });
 * ```
 */
const getBasicAuthHeader = (): string => {
  const credentials = `${API_USERNAME}:${API_PASSWORD}`;
  return `Basic ${btoa(credentials)}`;
};

/**
 * Instances des APIs spécialisées
 * 
 * Ces instances sont pré-configurées avec l'authentification HTTP Basic
 * et pointent vers l'URL de base configurée.
 * 
 * - `clientsApi` : Gestion des clients
 * - `requestsApi` : Gestion des demandes d'enrichissement
 * - `usersApi` : Gestion des utilisateurs
 * - `healthApi` : Vérification de l'état de l'API
 */
export const clientsApi = new ClientsApi(apiClient);
export const requestsApi = new RequestsApi(apiClient);
export const usersApi = new UsersApi(apiClient);
export const healthApi = new HealthApi(apiClient);

/**
 * Export du client API et des utilitaires pour configuration personnalisée
 * 
 * - `apiClient` : Instance du client API (pour configuration avancée)
 * - `configureAuth` : Fonction pour mettre à jour les credentials d'authentification
 * - `API_BASE_URL` : URL de base de l'API (pour référence)
 * - `getBasicAuthHeader` : Fonction utilitaire pour générer le header Authorization
 */
/**
 * Réponse de l'endpoint de détection des populations d'un fichier CRM.
 * Miroir du contrat API (.agents/contract.md §1).
 */
export interface CrmPopulationsResponse {
  has_population: boolean;
  populations: string[];
}

/**
 * Récupère la présence et les valeurs distinctes de la colonne `population`
 * pour un fichier CRM donné.
 *
 * GET /clients/{client_id}/crm-files/{crm_file}/populations
 *
 * Utilise un appel `fetch` direct (comme `launch-job` / `status` dans
 * Configuration.tsx) car cet endpoint n'est pas encore exposé par le client
 * généré `@enrichment/enrichment-api-client`.
 *
 * Rétro-compatibilité : en cas d'erreur (404, endpoint absent, etc.), on
 * retourne `{ has_population: false, populations: [] }` -> comportement actuel.
 */
const fetchCrmPopulations = async (
  clientId: number,
  crmFile: string
): Promise<CrmPopulationsResponse> => {
  const url = `${API_BASE_URL.replace(/\/$/, '')}/clients/${clientId}/crm-files/${encodeURIComponent(
    crmFile
  )}/populations`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: getBasicAuthHeader(),
      },
    });

    if (!response.ok) {
      return { has_population: false, populations: [] };
    }

    const data = await response.json();
    return {
      has_population: Boolean(data?.has_population),
      populations: Array.isArray(data?.populations) ? data.populations : [],
    };
  } catch {
    return { has_population: false, populations: [] };
  }
};

export { apiClient, configureAuth, API_BASE_URL, getBasicAuthHeader, fetchCrmPopulations };

